import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../context/ThemeContext';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const createVehicleIcon = (color = '#FF007F', vehicle = 'car') => {
  const paths = {
    car: 'M5 11l1.5-4.5a2 2 0 011.9-1.5h7.2a2 2 0 011.9 1.5L19 11m-14 0h14m-14 0v6a1 1 0 001 1h1a1 1 0 001-1v-1h8v1a1 1 0 001 1h1a1 1 0 001-1v-6M7 15h.01M17 15h.01',
    suv: 'M3 12l2-6a2 2 0 012-1.5h10a2 2 0 012 1.5l2 6m-18 0h18m-18 0v5a1 1 0 001 1h1a1 1 0 001-1v-1h12v1a1 1 0 001 1h1a1 1 0 001-1v-5M6 15h.01M18 15h.01',
    moto: 'M6 17a2 2 0 100-4 2 2 0 000 4zm12 0a2 2 0 100-4 2 2 0 000 4zm-6-3l3-6h-3l-2 4v2m3-6l-3-3m3 3h3'
  };
  const iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="11" fill="white" stroke="${color}" stroke-width="1"/><path d="${paths[vehicle] || paths.car}" stroke="${color}"/></svg>`;
  return L.divIcon({
    html: iconSvg,
    className: 'vehicle-marker',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

const createPinIcon = (color, label) => {
  const iconSvg = `<div style="position:relative"><svg xmlns="http://www.w3.org/2000/svg" width="32" height="42" viewBox="0 0 24 32"><path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 20 12 20s12-12 12-20c0-6.6-5.4-12-12-12z" fill="${color}" stroke="white" stroke-width="1"/><circle cx="12" cy="12" r="5" fill="white"/><text x="12" y="16" font-family="Arial" font-size="10" font-weight="bold" fill="${color}" text-anchor="middle">${label}</text></svg></div>`;
  return L.divIcon({
    html: iconSvg,
    className: 'pin-marker',
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -42]
  });
};

const RecenterMap = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

const MapView = ({
  pickup = null,
  dropoff = null,
  vehiclePosition = null,
  route = null,
  onLocationSelect = null,
  clickable = false,
  mapTheme = 'auto',
  vehicleColor = '#FF007F',
  vehicleIcon = 'car',
  height = '400px',
  markers = [],
  draggableMarkers = false,
  onPickupDrag = null,
  onDropoffDrag = null
}) => {
  const { resolvedTheme } = useTheme();
  const [center, setCenter] = useState([-0.1807, -78.4678]); // Quito default
  const [zoom, setZoom] = useState(13);
  const [selectedPoint, setSelectedPoint] = useState(null);

  // Determine effective theme
  const effectiveTheme = mapTheme === 'auto' ? resolvedTheme : mapTheme;

  const tileUrl = effectiveTheme === 'dark'
    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

  useEffect(() => {
    if (pickup && pickup.lat && pickup.lng) {
      setCenter([pickup.lat, pickup.lng]);
    } else if (vehiclePosition && vehiclePosition.lat) {
      setCenter([vehiclePosition.lat, vehiclePosition.lng]);
    }
  }, [pickup, vehiclePosition]);

  const MapClickHandler = () => {
    const map = useMap();
    useEffect(() => {
      if (!clickable) return;
      const handleClick = (e) => {
        const point = { lat: e.latlng.lat, lng: e.latlng.lng };
        setSelectedPoint(point);
        if (onLocationSelect) onLocationSelect(point);
      };
      map.on('click', handleClick);
      return () => map.off('click', handleClick);
    }, [map]);
    return null;
  };

  return (
    <div style={{ height, width: '100%', borderRadius: '1.5rem', overflow: 'hidden' }} data-testid="map-container">
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url={tileUrl}
        />
        <RecenterMap center={center} />
        <MapClickHandler />

        {pickup && pickup.lat && (
          <Marker
            position={[pickup.lat, pickup.lng]}
            icon={createPinIcon('#00F0FF', 'A')}
            draggable={draggableMarkers}
            eventHandlers={{
              dragend: (event) => {
                const next = event.target.getLatLng();
                if (onPickupDrag) onPickupDrag({ ...pickup, lat: next.lat, lng: next.lng });
              }
            }}
          >
            <Popup>Origen: {pickup.address || 'Ubicación de recogida'}</Popup>
          </Marker>
        )}

        {dropoff && dropoff.lat && (
          <Marker
            position={[dropoff.lat, dropoff.lng]}
            icon={createPinIcon('#FF007F', 'B')}
            draggable={draggableMarkers}
            eventHandlers={{
              dragend: (event) => {
                const next = event.target.getLatLng();
                if (onDropoffDrag) onDropoffDrag({ ...dropoff, lat: next.lat, lng: next.lng });
              }
            }}
          >
            <Popup>Destino: {dropoff.address || 'Ubicación de destino'}</Popup>
          </Marker>
        )}

        {vehiclePosition && vehiclePosition.lat && (
          <Marker
            position={[vehiclePosition.lat, vehiclePosition.lng]}
            icon={createVehicleIcon(vehicleColor, vehicleIcon)}
          >
            <Popup>Vehículo</Popup>
          </Marker>
        )}

        {selectedPoint && clickable && (
          <Marker position={[selectedPoint.lat, selectedPoint.lng]} icon={createPinIcon('#8A2BE2', '?')}>
            <Popup>Ubicación seleccionada</Popup>
          </Marker>
        )}

        {markers.map((m, idx) => (
          <Marker key={idx} position={[m.lat, m.lng]} icon={createPinIcon(m.color || '#8A2BE2', m.label || '•')}>
            <Popup>{m.popup || m.address}</Popup>
          </Marker>
        ))}

        {route && route.length > 1 && (
          <Polyline positions={route} color={vehicleColor} weight={4} opacity={0.7} />
        )}

        {pickup && dropoff && !route && (
          <Polyline
            positions={[[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]]}
            color={vehicleColor}
            weight={3}
            opacity={0.5}
            dashArray="10, 10"
          />
        )}
      </MapContainer>
    </div>
  );
};

export default MapView;
